import { useState, useEffect } from 'react';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle, Building2, Users } from 'lucide-react';

export function UnassignedSessions({ facilities, onAdmissionCreated }) {
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(null);

  useEffect(() => {
    api.get('/admin/assessments/unassigned')
      .then(r => setAssessments(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const assignFacility = async (assessment, facilityId, parentInWater) => {
    setAssigning(assessment.assessment_id);
    try {
      await api.put(`/admin/assessments/${assessment.assessment_id}/assign-facility`, {
        facility_id: facilityId,
        parent_in_water: parentInWater,
      });
      toast.success(`Facility assigned to Session #${assessment.session_number}`);
      setAssessments(prev => prev.filter(a => a.assessment_id !== assessment.assessment_id));
      onAdmissionCreated?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to assign'); }
    finally { setAssigning(null); }
  };

  if (loading || assessments.length === 0) return null;

  return (
    <Card className="rounded-2xl border-amber-200 bg-amber-50/50 mb-6" data-testid="unassigned-sessions">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2" style={{ fontFamily: 'Nunito' }}>
          <AlertTriangle className="w-4 h-4 text-amber-500" />
          {assessments.length} Past Session{assessments.length !== 1 ? 's' : ''} Without Facility
        </CardTitle>
        <p className="text-xs text-slate-500">Assign a facility to create admission records for these sessions</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {assessments.map(a => (
          <AssessmentRow
            key={a.assessment_id}
            assessment={a}
            facilities={facilities}
            assigning={assigning === a.assessment_id}
            onAssign={assignFacility}
          />
        ))}
      </CardContent>
    </Card>
  );
}

function AssessmentRow({ assessment, facilities, assigning, onAssign }) {
  const [selectedFac, setSelectedFac] = useState(facilities[0]?.facility_id || '');
  const [parentInWater, setParentInWater] = useState(assessment.parent_in_water || false);
  const dateLabel = assessment.date ? new Date(assessment.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Unknown';

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`unassigned-${assessment.assessment_id}`}>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-slate-800 truncate">
          Session #{assessment.session_number} — {assessment.child_name}
        </p>
        <p className="text-xs text-slate-400">{dateLabel}</p>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <select
          value={selectedFac}
          onChange={e => setSelectedFac(e.target.value)}
          className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs font-semibold text-slate-700 bg-white"
          data-testid={`select-fac-${assessment.assessment_id}`}
        >
          {facilities.map(f => (
            <option key={f.facility_id} value={f.facility_id}>{f.facility_name}</option>
          ))}
          <option value="private">Private Pool</option>
        </select>
        <button
          onClick={() => setParentInWater(!parentInWater)}
          className={`flex items-center gap-1 px-2 py-1.5 rounded-lg border text-xs font-semibold transition-colors ${parentInWater ? 'bg-[#ECFEFF] border-[#22D3EE] text-[#0E7490]' : 'bg-white border-slate-200 text-slate-500'}`}
          data-testid={`piw-toggle-${assessment.assessment_id}`}
        >
          <Users className="w-3 h-3" /> Parent
        </button>
        <Button
          size="sm"
          onClick={() => onAssign(assessment, selectedFac, parentInWater)}
          disabled={assigning}
          className="rounded-lg bg-[#1E1B4B] hover:bg-[#2D2B66] text-white text-xs font-bold gap-1 h-8"
          data-testid={`assign-btn-${assessment.assessment_id}`}
        >
          <CheckCircle className="w-3 h-3" /> Assign
        </Button>
      </div>
    </div>
  );
}
